import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog, { useSheetLayout } from '../common/Dialog';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../utils/apiConfig';
import CardNewsBody from './CardNewsBody';
import TextNoticeBody from './TextNoticeBody';
import {
  getWeekHideDays,
  hideAnnouncements,
  isAnnouncementVisible,
  isCardNews,
  isPrerendering,
  markAccountAnnouncementShown,
  markPrimaryAction,
  trackAnnouncement,
} from './announcementStorage';
import { isFirstVisitSession } from './firstVisit';
import { isWhatsNewOpen, markNoticePopupShown } from './popupGate';
import './AnnouncementModal.css';

export {
  ANNOUNCEMENT_CLOSE_HIDE_DAYS,
  ANNOUNCEMENT_WEEK_HIDE_DAYS,
  getAnnouncementHideUntilKey,
  getAnnouncementStorageKey,
  getCardNewsStorageKeys,
  hideAnnouncementForDays,
  shouldShowAnnouncement,
} from './announcementStorage';

/**
 * 사이트 공지 팝업 — 텍스트 공지와 카드뉴스를 한 팝업에서 탭으로 넘겨 본다.
 *
 * <AnnouncementModal announcements={getVisibleAnnouncements(pathname)} />
 *
 * - 공지가 2개 이상이면 위쪽에 탭 칩과 "다음 공지" 버튼이 생긴다. 1개면 탭 없이 그 공지만 보인다.
 * - 닫기(X·스크림·ESC·끌어 내리기): 묶음 안 모든 공지를 각자 closeHideDays(기본 1일)만큼 숨긴다.
 * - 일주일간 안보기: 모든 공지를 각자 dismissDays(기본 7일)만큼 숨긴다. 새 공지를 추가하면 그 공지만 다시 뜬다.
 * - CTA: 그 공지는 카드뉴스면 영구, 텍스트면 닫기 기간만큼 숨기고, 나머지는 닫기 기간만큼 숨긴 뒤 이동한다.
 * - 계정 1회 공지(oncePerAccount)는 로그인 후 서버 claim을 통과했을 때만 묶음에 들어간다.
 * - 처음 온 비로그인 방문자의 첫 세션에는 띄우지 않는다 (firstVisit.js).
 * 데스크톱은 중앙 다이얼로그, 모바일(≤600px)은 핸들 있는 바텀시트(Dialog가 처리).
 */
const DEFAULT_OPEN_DELAY_MS = 1200;
const WEEK_DAYS = 7;

const claimAccountAnnouncement = async (announcement, currentUser) => {
  if (typeof currentUser?.getIdToken !== 'function') return true;
  const token = await currentUser.getIdToken();
  const response = await fetch(
    getApiUrl(`/api/user/announcements/${announcement.id}/claim`),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error('announcement_claim_failed');
  const data = await response.json();
  return data.should_show === true;
};

// 버튼 설정({ action, path, url, onClick })을 실행한다.
const runButtonAction = (button, navigate) => {
  if (!button) return;
  if (button.action === 'navigate' && button.path) {
    navigate(button.path);
  } else if (button.action === 'link' && button.url) {
    window.open(button.url, '_blank', 'noopener,noreferrer');
  } else if (typeof button.onClick === 'function') {
    button.onClick();
  }
};

const getTabLabel = (item) => item.tabLabel || item.badge || item.title;

const getWeekHideLabel = (items) => {
  const days = items.map(getWeekHideDays);
  return days.every(value => value === WEEK_DAYS) ? '일주일간 안보기' : `${Math.max(...days)}일간 안보기`;
};

const AnnouncementModal = ({ announcements }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || '';
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const isSheet = useSheetLayout(isOpen);
  const claimCacheRef = useRef(new Map());
  const viewedIdsRef = useRef(new Set());
  const tabRefs = useRef([]);

  const candidates = Array.isArray(announcements) ? announcements.filter(item => item?.id) : [];
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;
  // 같은 공지 묶음이면 경로가 바뀌어도 열린 팝업을 다시 띄우지 않는다.
  const signature = candidates.map(item => item.id).join('|');

  useEffect(() => {
    setIsOpen(false);
    setItems([]);
    setActiveIndex(0);
    viewedIdsRef.current = new Set();
    if (!signature || isPrerendering()) return undefined;
    // 처음 온 비로그인 방문자는 가치를 먼저 경험해야 하므로 공지를 띄우지 않는다.
    if (!userId && isFirstVisitSession()) return undefined;

    let cancelled = false;
    let timer = null;
    const localItems = candidatesRef.current.filter(item => isAnnouncementVisible(item, userId));
    if (localItems.length === 0) return undefined;

    const resolveItem = (item) => {
      if (!item.oncePerAccount) return Promise.resolve(true);
      const claimKey = `${item.id}:${userId}`;
      if (!claimCacheRef.current.has(claimKey)) {
        claimCacheRef.current.set(
          claimKey,
          claimAccountAnnouncement(item, currentUserRef.current)
            .catch(() => true)
            .then((shouldShow) => {
              markAccountAnnouncementShown(item, userId);
              return shouldShow;
            })
        );
      }
      return claimCacheRef.current.get(claimKey);
    };

    Promise.all(localItems.map(resolveItem)).then((results) => {
      if (cancelled) return;
      // 계정 1회 공지는 claim 순간 소진되므로 반드시 보이는 첫 탭에 둔다.
      const readyItems = localItems.filter((_, index) => results[index]);
      readyItems.sort((a, b) => Number(Boolean(b.oncePerAccount)) - Number(Boolean(a.oncePerAccount)));
      if (readyItems.length === 0) return;
      // 진입 직후 바로 띄우지 않고 잠시 뒤에 연다 (화면이 자리 잡은 뒤 안내).
      timer = setTimeout(() => {
        if (cancelled || isWhatsNewOpen()) return;
        markNoticePopupShown();
        setItems(readyItems);
        setActiveIndex(0);
        setIsOpen(true);
      }, readyItems[0].delay ?? DEFAULT_OPEN_DELAY_MS);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [signature, userId]);

  const activeItem = isOpen ? items[activeIndex] : null;

  // 탭을 처음 볼 때 한 번만 노출로 센다.
  const markViewed = useCallback((item) => {
    if (!item || viewedIdsRef.current.has(item.id)) return;
    viewedIdsRef.current.add(item.id);
    trackAnnouncement(item, 'impression');
  }, []);

  useEffect(() => {
    markViewed(activeItem);
  }, [activeItem, markViewed]);

  // 채널톡 버튼이 바텀시트 버튼을 가리지 않게 한다.
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    document.body.classList.add('announcement-modal-open');
    return () => document.body.classList.remove('announcement-modal-open');
  }, [isOpen]);

  const trackViewedDismiss = useCallback((targets, source) => {
    targets.forEach((item) => {
      if (viewedIdsRef.current.has(item.id)) {
        trackAnnouncement(item, 'dismiss', { dismiss_source: source });
      }
    });
  }, []);

  const closeAll = useCallback((mode, source) => {
    // 노출 effect보다 닫기가 먼저 와도 보고 있던 공지의 노출·닫기는 남긴다.
    markViewed(items[activeIndex]);
    hideAnnouncements(items, mode, userId);
    trackViewedDismiss(items, source);
    setIsOpen(false);
  }, [activeIndex, items, markViewed, trackViewedDismiss, userId]);

  if (!activeItem) return null;

  const hasMultiple = items.length > 1;
  const isLast = activeIndex === items.length - 1;
  const { primaryButton } = activeItem;
  const primaryLabel = primaryButton?.text || (isCardNews(activeItem) ? '자세히 보기' : '');
  const reentryHint = activeItem.dismissToast?.message || null;
  const socialLinks = isCardNews(activeItem) ? (activeItem.socialLinks || []) : [];
  const hasNotes = Boolean(activeItem.footer || reentryHint || socialLinks.length);

  const handlePrimaryAction = () => {
    const others = items.filter(item => item !== activeItem);
    markViewed(activeItem);
    markPrimaryAction(activeItem, userId);
    trackAnnouncement(activeItem, 'cta');
    hideAnnouncements(others, 'close', userId);
    trackViewedDismiss(others, 'other_cta');
    setIsOpen(false);
    runButtonAction(primaryButton, navigate);
  };

  const handleSocialLink = (link, index) => {
    trackAnnouncement(activeItem, 'social', { link_index: index + 1 });
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const focusTab = (index) => {
    setActiveIndex(index);
    tabRefs.current[index]?.focus();
  };

  const handleTabKeyDown = (event) => {
    const lastIndex = items.length - 1;
    const keyTargets = {
      ArrowRight: activeIndex === lastIndex ? 0 : activeIndex + 1,
      ArrowLeft: activeIndex === 0 ? lastIndex : activeIndex - 1,
      Home: 0,
      End: lastIndex,
    };
    if (!(event.key in keyTargets)) return;
    event.preventDefault();
    focusTab(keyTargets[event.key]);
  };

  const panelId = `announcement-panel-${activeItem.id}`;
  const panelClassName = [
    'announcement-modal',
    isSheet && 'announcement-modal--sheet',
    hasMultiple && 'announcement-modal--multi',
  ].filter(Boolean).join(' ');

  const footer = (
    <div className="announcement-modal__actions" data-testid="announcement-actions">
      {primaryLabel && (
        <button
          className="announcement-modal__primary"
          data-testid="announcement-actions-primary"
          onClick={handlePrimaryAction}
          type="button"
        >
          {primaryLabel}
        </button>
      )}
      <div className="announcement-modal__subactions">
        <button
          className="announcement-modal__text-button"
          data-testid="announcement-actions-week"
          onClick={() => closeAll('week', 'hide_button')}
          type="button"
        >
          {getWeekHideLabel(items)}
        </button>
        {hasMultiple && !isLast && (
          <button
            className="announcement-modal__text-button announcement-modal__text-button--next"
            onClick={() => setActiveIndex(activeIndex + 1)}
            type="button"
          >
            다음 공지
            <svg aria-hidden="true" fill="none" height="16" viewBox="0 0 24 24" width="16">
              <path d="M9 5l7 7-7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
          </button>
        )}
      </div>
      {hasNotes && (
        <div className="announcement-modal__notes">
          {activeItem.footer && <p>{activeItem.footer}</p>}
          {reentryHint && <p data-testid="announcement-reentry-hint">{reentryHint}</p>}
          {socialLinks.length > 0 && (
            <div aria-label="관련 링크" className="announcement-modal__links" role="group">
              {socialLinks.map((link, index) => (
                <React.Fragment key={link.url}>
                  {index > 0 && <span aria-hidden="true" className="announcement-modal__link-divider">·</span>}
                  <button
                    className="announcement-modal__link"
                    onClick={() => handleSocialLink(link, index)}
                    type="button"
                  >
                    {link.text}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      closeLabel="닫기"
      dragHandle
      footer={footer}
      hideTitle
      onClose={() => closeAll('close', 'dismiss')}
      open={isOpen}
      panelClassName={panelClassName}
      size="sm"
      testId="announcement-modal"
      title={activeItem.title}
    >
      {hasMultiple && (
        <div aria-label="공지 목록" className="announcement-modal__tabs" role="tablist">
          {items.map((item, index) => (
            <button
              aria-controls={`announcement-panel-${item.id}`}
              aria-selected={index === activeIndex}
              className="announcement-modal__tab"
              id={`announcement-tab-${item.id}`}
              key={item.id}
              onClick={() => setActiveIndex(index)}
              onKeyDown={handleTabKeyDown}
              ref={(node) => { tabRefs.current[index] = node; }}
              role="tab"
              tabIndex={index === activeIndex ? 0 : -1}
              type="button"
            >
              {getTabLabel(item)}
            </button>
          ))}
        </div>
      )}
      <div
        aria-labelledby={hasMultiple ? `announcement-tab-${activeItem.id}` : undefined}
        className="announcement-modal__page"
        id={panelId}
        key={activeItem.id}
        role={hasMultiple ? 'tabpanel' : undefined}
      >
        {isCardNews(activeItem)
          ? <CardNewsBody announcement={activeItem} showArrows={!isSheet} />
          : <TextNoticeBody announcement={activeItem} />}
      </div>
    </Dialog>
  );
};

export default AnnouncementModal;
